import * as React from "react"
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Settings,
} from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { useBookingT } from "@/hooks/use-translations"
import type {
  ActivityEvent,
  ActivityEventType,
} from "@/features/integrations/api/types"

const ICONS: Record<ActivityEventType, React.ReactNode> = {
  connected: <CheckCircle2 className="size-4 text-success-500" />,
  sync_completed: <RefreshCw className="size-4 text-brand-500" />,
  error: <AlertTriangle className="size-4 text-error-500" />,
  config_changed: <Settings className="size-4 text-neutral-700" />,
}

const LABEL_KEYS: Record<ActivityEventType, { key: string; fallback: string }> = {
  connected: { key: "integrations_event_connected", fallback: "Connected" },
  sync_completed: { key: "integrations_event_sync_completed", fallback: "Sync completed" },
  error: { key: "integrations_event_error", fallback: "Error" },
  config_changed: { key: "integrations_event_config_changed", fallback: "Configuration changed" },
}

type Props = {
  events: ActivityEvent[]
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

export function ActivityLog({ events }: Props) {
  const t = useBookingT()
  const locale = (typeof document !== "undefined" ? document.documentElement.lang : "en") || "en"

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <h2 className="text-lg font-semibold text-neutral-900">
          {t("integrations_detail_activity", "Activity")}
        </h2>

        {events.length === 0 ? (
          <p className="text-sm text-neutral-600">
            {t("integrations_activity_empty", "No activity yet")}
          </p>
        ) : (
          <ul className="space-y-3">
            {events.map((evt) => {
              const meta = LABEL_KEYS[evt.type]
              return (
                <li key={evt.id} className="flex items-start gap-3">
                  <div className="mt-0.5 shrink-0">{ICONS[evt.type]}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-neutral-900">
                        {t(meta.key, meta.fallback)}
                      </span>
                      <span className="text-xs text-neutral-600">
                        {formatDate(evt.at, locale)}
                      </span>
                    </div>
                    {evt.details && (
                      <p className="text-sm text-neutral-600">{evt.details}</p>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
